
import { defineComponent, onBeforeUnmount, onMounted, ref } from "vue";

const HCAPTCHA_SCRIPT_ID = "hcaptcha-api-script";
const HCAPTCHA_SCRIPT_URL =
  "https://js.hcaptcha.com/1/api.js?render=explicit&hl=pt-BR";

function waitForHCaptcha(): Promise<any> {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const timer = window.setInterval(() => {
      const hcaptcha = (window as any).hcaptcha;

      if (hcaptcha) {
        window.clearInterval(timer);
        resolve(hcaptcha);
        return;
      }

      attempts += 1;
      if (attempts >= 160) {
        window.clearInterval(timer);
        reject(new Error("Tempo limite ao carregar hCaptcha."));
      }
    }, 50);
  });
}

function ensureHCaptchaScript() {
  const existingScript = document.getElementById(HCAPTCHA_SCRIPT_ID);

  if (existingScript) {
    return;
  }

  const script = document.createElement("script");
  script.id = HCAPTCHA_SCRIPT_ID;
  script.src = HCAPTCHA_SCRIPT_URL;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

export default defineComponent({
  name: "HcaptchaWidget",
  props: {
    siteKey: {
      type: String,
      required: true,
    },
  },
  emits: ["verify", "expired", "error"],
  setup(props, { emit, expose }) {
    const captchaContainer = ref<HTMLElement | null>(null);
    const loadError = ref(false);
    let widgetId: string | number | null = null;

    const renderCaptcha = async () => {
      if (!props.siteKey || !captchaContainer.value || widgetId !== null) {
        return;
      }

      try {
        ensureHCaptchaScript();
        const hcaptcha = await waitForHCaptcha();

        widgetId = hcaptcha.render(captchaContainer.value, {
          sitekey: props.siteKey,
          size: window.innerWidth <= 420 ? "compact" : "normal",
          callback: (token: string) => emit("verify", token),
          "expired-callback": () => emit("expired"),
          "error-callback": () => emit("error"),
        });
      } catch {
        loadError.value = true;
        emit("error");
      }
    };

    const reset = () => {
      const hcaptcha = (window as any).hcaptcha;

      if (hcaptcha && widgetId !== null) {
        hcaptcha.reset(widgetId);
      }
    };

    expose({ reset });

    onMounted(renderCaptcha);

    onBeforeUnmount(() => {
      const hcaptcha = (window as any).hcaptcha;

      if (hcaptcha && widgetId !== null && typeof hcaptcha.remove === "function") {
        hcaptcha.remove(widgetId);
      }
    });

    return {
      captchaContainer,
      loadError,
    };
  },
});
